import { useState } from 'react';
import {
  formatMm,
  thicknessAxisOf,
  explodeRepeat,
  duplicatePanelAlong,
  assemblyBox,
  defaultPanel,
  nextPanelKey,
  planPanelRemoval,
  removePanelWithShelves,
  resizeAssembly,
  type PanelRemovalPlan,
  type Axis,
  type AssemblyInstance,
  type AssemblyPanel,
  type MaterialSlot,
  type PanelOrientation,
} from '@scs/domain-engine';
import { MmInput } from '../lib/ui';
import { evalMm } from '../lib/mmexpr';
import type { EditorStore } from './store';

type Exec = EditorStore['exec'];

const ORIENT_LABEL: Record<PanelOrientation, string> = {
  horizontal: 'Horizontalna (dno/polica)',
  'vertical-side': 'Vertikalna bočna (stranica)',
  'vertical-front': 'Vertikalna čeona (leđa/front)',
};

const SLOT_LABEL: Record<MaterialSlot, string> = { carcass: 'Korpus', front: 'Fronte', back: 'Leđa', 'drawer-box': 'Kutija ladice', 'drawer-bottom': 'Dno ladice', plinth: 'Sokla' };

/** Panel key of a generated part (`key` or `key-N` for repeats). */
export function panelKeyOfPart(inst: AssemblyInstance, localKey: string): string | undefined {
  return inst.panels.find((p) => p.key === localKey)?.key ?? inst.panels.find((p) => p.repeat && localKey.startsWith(`${p.key}-`))?.key;
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="prop-row">
      <label>{label}</label>
      {children}
    </div>
  );
}

/** `view`: the whole assembly (outliner row) or one of its panels — never both. */
export function AssemblyProperties({
  inst,
  partLocalKey,
  view,
  exec,
  disabled,
  onSelectPanel,
}: {
  inst: AssemblyInstance;
  partLocalKey: string | null;
  view: 'assembly' | 'panel';
  exec: Exec;
  disabled: boolean;
  onSelectPanel(localKey: string | null): void;
}) {
  const lock = disabled || inst.locked;
  const [sizeError, setSizeError] = useState<string | null>(null);
  /** Deleting a panel that shelves hang on: choices before the delete. */
  /** "Umnoži ovu": offset typed for the copy (null = prompt closed). */
  const [copyOffset, setCopyOffset] = useState<string | null>(null);
  const copyPanel = () => {
    const mmValue = copyOffset === null ? null : evalMm(copyOffset);
    if (mmValue === null || !key) return;
    let newKey = '';
    editA('Umnoži ploču', (a) => {
      newKey = duplicatePanelAlong(a, key, Math.round(mmValue * 1000))?.key ?? '';
    });
    setCopyOffset(null);
    // The copy is selected right away so it can be moved further.
    if (newKey) onSelectPanel(newKey);
  };
  const [removal, setRemoval] = useState<{ key: string; plan: PanelRemovalPlan; keep: Record<string, 'first' | 'second'> } | null>(null);
  const removePanel = (k: string, keep: Record<string, 'first' | 'second'> = {}) => {
    editA('Obriši ploču', (a) => removePanelWithShelves(a, k, keep));
    setRemoval(null);
    onSelectPanel(null);
  };
  const box = assemblyBox(inst);
  // Overall size: all panels follow and keep their relations (thicknesses, faces, recesses, dividers).
  const resize = (axis: Axis, v: number) => {
    const r = resizeAssembly(inst, axis, v);
    if ('error' in r) return setSizeError(r.error);
    setSizeError(null);
    exec(
      'Dimenzije sklopa',
      (d) => {
        const i = d.assemblies.findIndex((x) => x.id === inst.id);
        if (i >= 0) d.assemblies[i] = { ...d.assemblies[i]!, panels: r.panels };
      },
      { pins: false },
    );
  };
  const editA = (label: string, fn: (a: AssemblyInstance) => void) => exec(label, (d) => fn(d.assemblies.find((x) => x.id === inst.id)!));
  const key = partLocalKey ? panelKeyOfPart(inst, partLocalKey) : undefined;
  const panel = key ? inst.panels.find((p) => p.key === key) : undefined;
  const editP = (label: string, fn: (p: AssemblyPanel) => void) =>
    editA(label, (a) => {
      fn(a.panels.find((p) => p.key === key)!);
    });

  return (
    <div>
      {removal && <RemovalDialog inst={inst} state={removal} onChange={setRemoval} onConfirm={() => removePanel(removal.key, removal.keep)} />}
      {view === 'assembly' && (
        <div className="prop-group">
          <h3>Sklop od ploča</h3>
          <Row label="Naziv">
            <input
              className="input"
              style={{ height: 26 }}
              defaultValue={inst.name}
              key={inst.name}
              disabled={lock}
              onKeyDown={(e) => e.stopPropagation()}
              onBlur={(e) => e.target.value.trim() && e.target.value !== inst.name && editA('Naziv sklopa', (a) => (a.name = e.target.value.trim().slice(0, 200)))}
            />
          </Row>
          <Row label="Ploča">
            <span>{inst.panels.length}</span>
          </Row>
          {inst.panels.length > 0 && (
            <>
              <Row label="Širina (X)">
                <MmInput label="Širina sklopa" value={box.size[0]} min={1} max={10000} disabled={lock} onCommit={(v) => resize('x', v)} />
              </Row>
              <Row label="Visina (Y)">
                <MmInput label="Visina sklopa" value={box.size[1]} min={1} max={10000} disabled={lock} onCommit={(v) => resize('y', v)} />
              </Row>
              <Row label="Dubina (Z)">
                <MmInput label="Dubina sklopa" value={box.size[2]} min={1} max={10000} disabled={lock} onCommit={(v) => resize('z', v)} />
              </Row>
              {sizeError ? (
                <p className="error-text" style={{ fontSize: 11 }}>
                  {sizeError}
                </p>
              ) : (
                <p className="faint" style={{ fontSize: 11, margin: '2px 0 0' }}>
                  Promjena mjere sklopa mijenja sve ploče i zadržava njihove odnose.
                </p>
              )}
            </>
          )}
          {inst.preset && (
            <Row label="Preset">
              <span className="faint">statički, v{inst.preset.version}</span>
            </Row>
          )}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
            <button
              className="btn sm"
              disabled={lock}
              onClick={() => {
                const k = nextPanelKey(inst);
                editA('Dodaj ploču', (a) => a.panels.push(defaultPanel(k)));
                onSelectPanel(k);
              }}
            >
              + Ploča
            </button>
          </div>
          {inst.preset && (
            <p className="faint" style={{ fontSize: 11 }}>
              Statički preset nema parametarska pravila — mjere se mijenjaju po ploči.
            </p>
          )}
        </div>
      )}
      {view === 'panel' && panel && (
        <div className="prop-group">
          <h3>Ploča: {panel.label}</h3>
          <p className="faint" style={{ fontSize: 11, margin: '0 0 4px' }}>
            u sklopu „{inst.name}”
          </p>
          <Row label="Naziv">
            <input
              className="input"
              style={{ height: 26 }}
              defaultValue={panel.label}
              key={panel.key + panel.label}
              disabled={lock}
              onKeyDown={(e) => e.stopPropagation()}
              onBlur={(e) => e.target.value.trim() && e.target.value !== panel.label && editP('Naziv ploče', (p) => (p.label = e.target.value.trim().slice(0, 200)))}
            />
          </Row>
          <Row label="Orijentacija">
            <select className="input" disabled={lock} value={panel.orientation} onChange={(e) => editP('Orijentacija ploče', (p) => (p.orientation = e.target.value as PanelOrientation))}>
              {(Object.keys(ORIENT_LABEL) as PanelOrientation[]).map((o) => (
                <option key={o} value={o}>
                  {ORIENT_LABEL[o]}
                </option>
              ))}
            </select>
          </Row>
          <Row label="Dužina">
            <MmInput label="Dužina" value={panel.length} min={1} max={5000} disabled={lock} onCommit={(v) => editP('Dužina ploče', (p) => (p.length = v))} />
          </Row>
          <Row label="Širina">
            <MmInput label="Širina" value={panel.width} min={1} max={5000} disabled={lock} onCommit={(v) => editP('Širina ploče', (p) => (p.width = v))} />
          </Row>
          <Row label="Debljina">
            <MmInput label="Debljina" value={panel.thickness} min={1} max={100} disabled={lock} onCommit={(v) => editP('Debljina ploče', (p) => (p.thickness = v))} />
          </Row>
          {(['X', 'Y', 'Z'] as const).map((ax, i) => (
            <Row key={ax} label={`Offset ${ax}`}>
              <MmInput label={`Offset ${ax}`} value={panel.position[i]!} min={-5000} max={5000} disabled={lock} onCommit={(v) => editP(`Pomjeranje ploče ${ax}`, (p) => (p.position[i] = v))} />
            </Row>
          ))}
          <Row label="Materijal">
            <select className="input" disabled={lock} value={panel.materialSlot} onChange={(e) => editP('Materijal ploče', (p) => (p.materialSlot = e.target.value as MaterialSlot))}>
              {(Object.keys(SLOT_LABEL) as MaterialSlot[]).map((s) => (
                <option key={s} value={s}>
                  {SLOT_LABEL[s]}
                </option>
              ))}
            </select>
          </Row>
          <Row label="God">
            <select className="input" disabled={lock} value={panel.grain} onChange={(e) => editP('God ploče', (p) => (p.grain = e.target.value as AssemblyPanel['grain']))}>
              <option value="length">Po dužini</option>
              <option value="width">Po širini</option>
              <option value="none">Bez goda</option>
            </select>
          </Row>
          {panel.repeat && panel.repeat.count > 1 && (
            <Row label="Ponavljanje">
              <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span className="faint">{panel.repeat.count} kom (stara opcija)</span>
                <button
                  className="btn sm"
                  disabled={lock}
                  title="Svaka kopija postaje zasebna obična ploča na istom mjestu"
                  onClick={() => editA('Razdvoji ponovljene ploče', (a) => explodeRepeat(a, key!))}
                >
                  Pretvori u zasebne ploče
                </button>
              </span>
            </Row>
          )}
          {copyOffset !== null && (
            <div className="copy-prompt">
              <span>Pomak kopije ({['X', 'Y', 'Z'][thicknessAxisOf(panel)]}, mm):</span>
              <input
                className="input"
                autoFocus
                value={copyOffset}
                aria-label="Pomak kopije u mm"
                title="Podrazumijevano debljina ploče — kopija legne uz original. Negativno = na drugu stranu. Može račun (18+2)."
                onFocus={(e) => e.target.select()}
                onChange={(e) => setCopyOffset(e.target.value)}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === 'Enter') copyPanel();
                  if (e.key === 'Escape') setCopyOffset(null);
                }}
              />
              <button className="btn sm primary" onClick={copyPanel}>
                Umnoži
              </button>
              <button className="btn sm ghost" onClick={() => setCopyOffset(null)}>
                Otkaži
              </button>
            </div>
          )}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
            <button className="btn sm" disabled={lock} onClick={() => setCopyOffset(formatMm(panel.thickness))} title="Zasebna kopija ove ploče, pomjerena po debljini">
              Umnoži ovu
            </button>
            <button
              className="btn sm ghost danger"
              disabled={lock}
              onClick={() => {
                // Shelves that hang on this panel: merge / extend silently, ask when there is a choice or a loss.
                const plan = planPanelRemoval(inst, key!);
                if (plan.conflicts.length || plan.orphans.length) setRemoval({ key: key!, plan, keep: {} });
                else removePanel(key!);
              }}
            >
              Obriši ploču
            </button>
          </div>
        </div>
      )}
      {!panel && (
        <p className="muted" style={{ padding: '0 12px' }}>
          Kliknite ploču u sceni ili listi za izmjenu mjera.
        </p>
      )}
    </div>
  );
}

/** Asks what happens to the shelves when the panel they hang on is deleted. */
function RemovalDialog({
  inst,
  state,
  onChange,
  onConfirm,
}: {
  inst: AssemblyInstance;
  state: { key: string; plan: PanelRemovalPlan; keep: Record<string, 'first' | 'second'> };
  onChange(s: typeof state | null): void;
  onConfirm(): void;
}) {
  const set = (id: string) => inst.shelfSets?.find((s) => s.id === id);
  const desc = (id: string) => {
    const s = set(id);
    return s ? `${s.count} ${s.count === 1 ? 'polica' : 'police'}, zazor ${s.gap / 1000} mm, debljina ${s.thickness / 1000} mm` : id;
  };
  const panel = inst.panels.find((p) => p.key === state.key);
  return (
    <div className="modal-backdrop" onClick={() => onChange(null)}>
      <div className="card modal" role="dialog" aria-modal aria-label="Brisanje ploče s policama" onClick={(e) => e.stopPropagation()}>
        <h2>Obriši „{panel?.label ?? state.key}”</h2>
        {state.plan.conflicts.map((c) => {
          const k = `${c.first}:${c.second}`;
          const choice = state.keep[k] ?? 'first';
          const [a, b] = c.axis === 'x' ? ['Lijevi', 'Desni'] : ['Donji', 'Gornji'];
          return (
            <div key={k} className="prop-group">
              <p>Police s obje strane se spajaju, ali nisu istog stila. Koji stil polica zadržati?</p>
              {(['first', 'second'] as const).map((side) => (
                <label key={side} style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '2px 0' }}>
                  <input type="radio" checked={choice === side} onChange={() => onChange({ ...state, keep: { ...state.keep, [k]: side } })} />
                  <span>
                    <strong>{side === 'first' ? a : b}</strong> — {desc(side === 'first' ? c.first : c.second)}
                  </span>
                </label>
              ))}
            </div>
          );
        })}
        {state.plan.orphans.length > 0 && (
          <p className="warn-text" style={{ fontSize: 12 }}>
            Upozorenje: {state.plan.orphans.map((id) => set(id)?.label ?? id).join(', ')} {state.plan.orphans.length === 1 ? 'ostaje' : 'ostaju'} bez ploče na koju se kače — biće obrisane zajedno s
            pločom.
          </p>
        )}
        <div className="actions">
          <button className="btn" onClick={() => onChange(null)}>
            Otkaži
          </button>
          <button className="btn primary danger" onClick={onConfirm}>
            Obriši ploču
          </button>
        </div>
      </div>
    </div>
  );
}
